import { Card, Col, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { ButtonLink } from '../../../components';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectSlackAvailable,
  selectSlackChannels,
  selectMsteamsAvailable,
  selectMsteamsChannels,
} from '../../../redux/selectors/integrationsSelectors';
import {
  selectAuthUser,
  selectAuthProfiles,
  selectAuthOnboardStep,
  selectAuthIsTeamOwner,
} from '../../../redux/selectors/authSelectors';
import {
  selectUserProfile,
  selectApplicationProfile,
} from '../../../redux/selectors/profileSelectors';

const StyledCard = styled(Card)`
  padding: 0 30px;
`;

const StyledTitle = styled(Card.Title)`
  align-items: center;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  padding: 15px 0;
`;

const StyledLabel = styled.span`
  color: #a3a5a9;
`;

const StyledNav = styled.nav`
  display: flex;
  border-top: 1px solid #e9ecef;
`;

const StyledNavItem = styled(Link)`
  ${({ active }) => {
    return `
    border-bottom: 2px solid transparent;
    border-color: ${active ? '#fcb12e' : 'transparent'};
    color: #4D5C6D !important;
    display: block;
    font-size: .875rem;
    text-decoration: none !important;
    padding: 15px 30px;
    transition: all .1s ease-out;

    &:hover {
      color: #fcb12e !important;
    }
  `;
  }}
`;


export const ViewUserProfile = () => {
  const user = useSelector(selectAuthUser);
  const profiles = useSelector(selectAuthProfiles);
  const onboardStep = useSelector(selectAuthOnboardStep);
  const isTeamOwner = useSelector(selectAuthIsTeamOwner);
  const userProfile = useSelector(selectUserProfile);
  const applicationProfile = useSelector(selectApplicationProfile);

  const slackAvailable = useSelector(selectSlackAvailable);
  const slackChannels = useSelector(selectSlackChannels);
  const msteamAvailable = useSelector(selectMsteamsAvailable);
  const msTeamChannels = useSelector(selectMsteamsChannels);

  // Return loading state if user data is not yet available
  if (!user) {
    return <div>Loading user profile...</div>;
  }

  // Only pull data from applicationProfile (except tech stack, tools, profileCount, isTeamOwner)
  const profile = {
    firstName: applicationProfile?.firstName || '-',
    lastName: applicationProfile?.lastName || '-',
    nickname: applicationProfile?.nickname || '-',
    industry: applicationProfile?.industry || '-',
    team: user.team || user.teamName || '-',
    onboardStatus: onboardStep || user.onboardStep || 0,
    jobFunction: applicationProfile?.jobFunction || '-',
    isTeamOwner: isTeamOwner || false,
    profileCount: profiles ? Object.keys(profiles).length : 0,
  };

  const technologies = {
    stack:
      userProfile?.techStack && userProfile.techStack.trim()
        ? userProfile.techStack.split(',').filter((tech) => tech.trim())
        : [],
    tools:
      userProfile?.tools && userProfile.tools.trim()
        ? userProfile.tools.split(',').filter((tool) => tool.trim())
        : [],
  };

  const integrations = {
    slack: {
      available: slackAvailable || false,
      channels: slackChannels ? slackChannels.length : 0,
    },
    msTeams: {
      available: msteamAvailable || false,
      channels: msTeamChannels ? msTeamChannels.length : 0,
    },
  };

  return (
    <div className='my-4'>
      <Row>
        <Col lg={11}>
          <Row className='my-3'>
            <Col lg={12}>
              <StyledCard className='h-100'>
                <StyledTitle>
                  <span>Profile</span>
                </StyledTitle>
                <div className='mt-2 mb-3'>
                  <Row className='py-2'>
                    <Col lg={8}>
                      <div className='mt-1'>
                        <h2>
                          {profile.firstName} {profile.lastName === '-' ? '' : profile.lastName}
                        </h2>
                        {profile.nickname === '-' ? '' : <span>@{profile.nickname}</span>}
                      </div>
                    </Col>
                    <Col lg={4}>
                      <div className='flex align-items-center justify-content-end mb-3 pt-3'>
                        <ButtonLink variant='primary' to='/profile/edit'>
                          Edit profile
                        </ButtonLink>
                      </div>
                    </Col>
                  </Row>
                </div>
              </StyledCard>
            </Col>
          </Row>

          <Row className='my-3'>
            <Col lg={12}>
              <StyledCard className='h-100'>
                <StyledTitle>
                  <span>Technologies</span>

                  {/* <ButtonLink to="edit" variant="secondary" small>Edit</ButtonLink> */}
                </StyledTitle>

                <div className='mt-2 mb-3'>
                  <Row className='py-2'>
                    <Col lg={2}>
                      <StyledLabel>Tech Stack</StyledLabel>
                    </Col>
                    <Col lg={8}>
                      {technologies.stack.length > 0
                        ? technologies.stack.join(', ')
                        : '-'}
                    </Col>
                  </Row>
                  <Row className='py-2'>
                    <Col lg={2}>
                      <StyledLabel>Tools</StyledLabel>
                    </Col>
                    <Col lg={8}>
                      {technologies.tools.length > 0
                        ? technologies.tools.join(', ')
                        : '-'}
                    </Col>
                  </Row>
                </div>
              </StyledCard>
            </Col>
          </Row>
          <Row className="my-3">
            <Col lg={12}>
              <StyledCard>
                <StyledTitle>
                  <span>Details</span>
                </StyledTitle>

                <div className="mt-2 mb-3">

                  <Row className="py-2">
                    <Col lg={2}><StyledLabel>First Name</StyledLabel></Col>
                    <Col lg={4}>{profile.firstName}</Col>

                    <Col lg={2}><StyledLabel>Last Name</StyledLabel></Col>
                    <Col lg={4}>{profile.lastName}</Col>
                  </Row>

                  <Row className="py-2">
                    <Col lg={2}><StyledLabel>Nickname</StyledLabel></Col>
                    <Col lg={4}>{profile.nickname}</Col>

                    <Col lg={2}><StyledLabel>Industry</StyledLabel></Col>
                    <Col lg={4}>{profile.industry}</Col>
                  </Row>

                  <Row className="py-2">
                    <Col lg={2}><StyledLabel>Job Function</StyledLabel></Col>
                    <Col lg={4}>{profile.jobFunction}</Col>

                    <Col lg={2}><StyledLabel>Team Owner</StyledLabel></Col>
                    <Col lg={4}>{profile.isTeamOwner ? "Yes" : "No"}</Col>
                  </Row>

                  <Row className="py-2">
                    <Col lg={2}><StyledLabel>Team</StyledLabel></Col>
                    <Col lg={4}>{profile.team}</Col>
                  </Row>

                </div>
              </StyledCard>
            </Col>
          </Row>

          <Row className='my-3'>
            <Col lg={12}>
              <StyledCard>
                <StyledTitle>
                  <span>Integrations</span>
                </StyledTitle>

                <div className='mt-2 mb-3'>
                  <Row className='py-2'>
                    <Col lg={2}>
                      <StyledLabel>Slack</StyledLabel>
                    </Col>
                    <Col lg={4}>
                      {integrations.slack.available ? (
                        <span>
                          Connected ({integrations.slack.channels} channels)
                        </span>
                      ) : (
                        <span>Not connected</span>
                      )}
                    </Col>
                    <Col lg={2}>
                      <StyledLabel>Microsoft Teams</StyledLabel>
                    </Col>
                    <Col lg={4}>
                      {integrations.msTeams.available ? (
                        <span>
                          Connected ({integrations.msTeams.channels} channels)
                        </span>
                      ) : (
                        <span>Not connected</span>
                      )}
                    </Col>
                  </Row>
                </div>
              </StyledCard>
            </Col>
          </Row>
        </Col>
      </Row>
    </div >
  );
};
